import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchInput from '@/components/feature/SearchInput';
import MediaCard from '@/components/feature/MediaCard';
import NewsRow from '@/components/feature/NewsRow';
import EmptyState from '@/components/feature/EmptyState';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getAllMedia, getNews, searchMedia, searchNews, sortNewsByDate } from '@/lib/catalog';

type Tab = 'all' | 'movie' | 'tv' | 'news';

const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'news', label: '资讯' }
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [tab, setTab] = useState<Tab>('all');

  useDocumentTitle(
    keyword ? `搜索"${keyword}" - 风驰影院` : '搜索 - 风驰影院',
    keyword ? `风驰影院搜索"${keyword}"的结果，包含电影、电视剧与影视资讯。` : '风驰影院全站搜索，查找电影、电视剧与影视资讯。'
  );

  useEffect(() => {
    setTab('all');
  }, [keyword]);

  const mediaResults = useMemo(() => {
    if (!keyword) return [];
    if (tab === 'movie') return searchMedia(keyword, 'movie');
    if (tab === 'tv') return searchMedia(keyword, 'tv');
    if (tab === 'news') return [];
    return searchMedia(keyword);
  }, [keyword, tab]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    if (tab === 'movie' || tab === 'tv') return [];
    return searchNews(keyword);
  }, [keyword, tab]);

  const totalCount = mediaResults.length + newsResults.length;

  const hotNews = sortNewsByDate(getNews()).slice(0, 5);
  const hotMedia = [...getAllMedia()].sort((a, b) => b.popularity - a.popularity).slice(0, 5);

  const handleSearch = (value: string) => {
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-10">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
        <Link to="/" className="transition hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">搜索</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">站内搜索</h1>
        <p className="mt-2 text-sm text-foreground-500">输入关键词，查找你感兴趣的电影、电视剧与影视资讯。</p>
        <div className="mt-4 max-w-2xl">
          <SearchInput defaultValue={keyword} size="lg" onSearch={handleSearch} />
        </div>
      </header>

      {keyword ? (
        <div className="mt-8">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-foreground-600">
              搜索"<span className="font-bold text-primary-600">{keyword}</span>"，共找到{' '}
              <span className="font-bold text-primary-600">{totalCount}</span> 条结果
            </p>
            <div className="flex flex-wrap gap-2">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition md:text-sm ${
                    tab === t.key
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {totalCount === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon="ri-search-line"
                title="未找到相关结果"
                description={`没有找到与"${keyword}"匹配的内容，换个关键词试试，或看看下面的热门推荐。`}
              />
              <div className="mt-8">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                  <i className="ri-fire-line text-accent-500" />
                  热门影视资讯
                </h2>
                <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                  {hotNews.map((item) => (
                    <NewsRow key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-8">
              {mediaResults.length > 0 && (
                <section>
                  <h2 className="mb-4 text-sm font-bold text-foreground-800">影视作品（{mediaResults.length}）</h2>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-5">
                    {mediaResults.map((item) => (
                      <MediaCard key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              )}

              {newsResults.length > 0 && (
                <section>
                  <h2 className="mb-4 text-sm font-bold text-foreground-800">影视资讯（{newsResults.length}）</h2>
                  <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                    {newsResults.map((item) => (
                      <NewsRow key={item.id} item={item} showSummary />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-2 flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
              <i className="ri-fire-line text-accent-500" />
              热门影视资讯
            </h2>
            {hotNews.map((item) => (
              <NewsRow key={item.id} item={item} />
            ))}
          </section>
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
              <i className="ri-movie-2-line text-primary-500" />
              热门影视作品
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {hotMedia.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}